import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileOnly } from 'react-device-detect';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { isEmpty, mapKeys, get } from 'lodash-es';
import {
  BaseBox,
  FlexBox,
  Icons,
  Body1,
  Body2,
  Heading3,
  Heading4,
  DoconListMenu,
  PrimaryButton,
  message,
} from '@docon/docon-ui-library';
import AddPatient from './AddPatient';
import AppointmentSuccess from './AppointmentSuccess';
import BookingLogin from './BookingLogin';
import DocItem from 'components/Doctors/DocItem';
import Image from 'shared/Image';
import { Status } from 'redux/reducer/auth';
import { getUserPatients } from 'redux/reducer/doctor';
import { bookAppointment, getTeleConsultPricing, updateAppointmentState } from 'redux/reducer/appointment';
import { getAgeFromDOB, getShiftType } from 'utils';
import { borderColor } from 'utilities/colorConfig';
import { Loader } from 'shared';
import { headerKey, restClient } from 'services';
import { cleverTapAnl } from 'utilities';
import { EventAppointmentPatientAdd, EventAppointmentPatientCreate } from 'utilities/clevertapEvent';

interface IProps {
  isReschedule?: boolean;
  appointment?: any;
  showBackArrow: boolean;
  activeClinicID: string;
  slot: any;
  activeDate: any;
  doctor: any;
  parentCallback: (x: any) => void;
  onBackArrowClick: () => void;
}

const Header = styled(FlexBox)`
  height: 100%;
  gap: 10px;
  align-items: center;
`;

const Root = styled(FlexBox)`
  height: 100%;
  padding: 1.5rem;
  gap: 16px;
  flex-direction: column;
  flex-wrap: nowrap;

  ${DocItem} {
    padding: 0;
    flex: 0 0 auto;

    ${Heading3} {
      font-weight: bold;
    }
  }

  ${Image} {
    height: 48px;
    width: 48px;
  }

  ${PrimaryButton} {
    margin-bottom: 0;
    margin-top: 0;

    &.confirmBtn {
      font-size: 18px;
      font-weight: 700;
    }
  }

  ${DoconListMenu} {
    width: 100%;
    padding-top: 16px;
    border-top: 1px solid ${borderColor};

    > div { background-color: transparent; }

    ${Heading4} {
      display: flex;
      align-items: baseline;
      gap: 4px;
      max-width: 200px;
    }
  }

  ${() => isMobileOnly && css`
    padding: 1rem;
  `}
`;

const ZeroPatient = styled(Body1)`
  flex: 1;
  display: grid;
  place-content: center;
  font-weight: bold;
`;

function ConfirmAppointment({
  showBackArrow,
  isReschedule,
  appointment,
  slot = {},
  activeDate,
  activeClinicID,
  doctor = {},
  parentCallback,
  onBackArrowClick,
}: IProps) {

  const {
    clinicByClinicId,
    isFetchingPatients,
    patientsByClinicID,
  } = useSelector(state => state.doctor);
  const { user, accessToken, userUpdateStatus } = useSelector(state => state.auth);
  const { onlineScheduleByClinicId } = useSelector(state => state.shift);
  const {
    isBookingAppointment,
    bookedAppointment,
    teleConsultPricing,
    isFetchingTeleConsultPricing,
    bookAppointmentErrors,
  } = useSelector(state => state.appointment);

  const patientSelectTrigger = useRef('auto');
  const doctorID = doctor?.doctorID || doctor?.docID;
  const activeClinic = clinicByClinicId[activeClinicID] || {};
  const patients = isEmpty(appointment) ? patientsByClinicID[activeClinicID] || [] : [appointment];
  const shiftByShiftID = mapKeys(onlineScheduleByClinicId[activeClinicID]?.shifts, 'shiftID');

  const [selectedPatient, setSelectedPatient] = useState<any>();
  const [showAddPatient, setShowAddPatient] = useState<any>(false);

  const dispatch = useDispatch();

  const patientsList = patients.map(({ patientDetails }) => ({
    value: patientDetails,
    title: (
      <>
        <BaseBox className='ellipsis' as='span'>{`${patientDetails?.firstName} ${patientDetails?.lastName || ''}`}</BaseBox>
        <Body2 tagAs='span' color='inherit' title={patientDetails.officeID ? ` (${patientDetails.officeID})` : ''} />
      </>
    ),
    subtitle: `${patientDetails?.sex}` + ', ' + getAgeFromDOB(patientDetails?.dateOfBirth || patientDetails?.estimatedDOB),
  }));

  useEffect(() => {
    if (user.userID && isEmpty(appointment) && isEmpty(patients)) {
      restClient.setHeader(headerKey.AccessToken, accessToken);
      getPatientsOnUserId();
    }
  }, [user.userID]);

  useEffect(() => {
    return () => {
      dispatch(updateAppointmentState({
        bookAppointmentErrors: {},
        teleConsultPricing: {},
      }));
    };
  }, []);

  useEffect(() => {
    if (!isReschedule && !isEmpty(bookedAppointment))
      triggerEvent('appointment_create');
  }, [bookedAppointment]);

  useEffect(() => {
    const errorMsg = get(bookAppointmentErrors, 'errorInfo.error_msg', bookAppointmentErrors?.error);
    if (errorMsg) {
      message.error(errorMsg, { autoClose: 3000 });
    }
  }, [bookAppointmentErrors]);

  useEffect(() => {
    if (selectedPatient) {
      if (slot.teleConsult)
        dispatch(getTeleConsultPricing({
          doctorID,
          patientID: selectedPatient.patientID,
          appointmentDate: moment(activeDate).format('YYYY-MM-DD'),
        }));
      else triggerEvent('appointment_patient_select');
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (!isEmpty(teleConsultPricing) && !isEmpty(selectedPatient))
      triggerEvent('appointment_patient_select');
  }, [teleConsultPricing]);

  useEffect(() => {
    if (!isEmpty(patients)) {
      setSelectedPatient(patients[0].patientDetails);
    }
  }, [patients]);

  useEffect(() => {
    parentCallback(
      <Header>
        {
          showBackArrow || showAddPatient ? <Icons.BackIcon onClick={handleBackArrowClick} /> : null
        }
        {
          showAddPatient ? 'Create a New Patient' : 'Confirm Appointment'
        }
      </Header>,
    );
  }, [showAddPatient]);

  function getPatientsOnUserId() {
    dispatch(getUserPatients({
      userID: user.userID,
      clinicID: activeClinic.clinicID,
      doctorID,
      mobileNumber: user.mobileNumber,
    }));
  }

  function submitAppointment() {
    if (isBookingAppointment) return;

    const payload: any = {
      slotNumber: slot.slotNumber,
      shiftID: slot.shiftID,
      doctorID,
      clinicID: activeClinicID,
      appointmentDate: moment(activeDate).format('YYYY-MM-DD'), // activeDate
      type: getShiftType(shiftByShiftID[slot.shiftID]),
      isTeleConsult: slot.teleConsult,
      source: 'online',
      userID: user.userID,
      userDetails: {
        firstName: user.firstName,
        lastName: user.lastName,
        mobileNumber: user.mobileNumber,
      },
      patientID: selectedPatient.patientID,
      patientDetails: {
        patientID: selectedPatient.patientID,
        firstName: selectedPatient.firstName,
        lastName: selectedPatient.lastName,
        mobileNumber: selectedPatient.mobileNumber,
      },
    };

    if (isReschedule) {
      payload.appointmentID = appointment.appointmentID;
      payload.tmpUserDetails = appointment.tmpUserDetails || {};
      payload.status = 'rescheduled';
      if (appointment.teleConsultSessionID)
        payload.teleConsultSessionID = appointment.teleConsultSessionID;
    }

    if (slot.teleConsult) {
      payload.tag = teleConsultPricing.tag;
      payload.pricing_details = teleConsultPricing.pricing_details;
      if (teleConsultPricing.tag === 'FOLLOWUP_CONSULT') {
        payload.is_followup_opted = true;
      }
    }
    dispatch(bookAppointment(payload));
  }

  function handleListMenuChange({ item }: any) {
    patientSelectTrigger.current = 'manual';
    setSelectedPatient(item.value);
  }

  const handleBtnClick = (action: string) => () => {
    switch (action) {
      case 'addPatient':
        patientSelectTrigger.current = 'auto';
        triggerEvent('appointment_patient_add');
        setShowAddPatient(true);
        break;
      case 'confirm':
        submitAppointment();
        break;
    }
  };

  function handleAddPatientSuccess() {
    triggerEvent('appointment_patient_create');
    setShowAddPatient(false);
    getPatientsOnUserId();
  }

  function handleBackArrowClick() {
    if (showAddPatient)
      return setShowAddPatient(false);

    onBackArrowClick();
  }

  function triggerEvent(eventName) {
    const event = {
      page_name: 'appointment_booking',
      slot_time: slot.parsedStartTime,
      slot_type: slot.teleConsult ? 'video' : 'physical',
      slot_rank: slot.rank,
      doctor_id: doctorID,
      appointment_date: moment(activeDate).format('YYYY-MM-DD'),
      transaction_amount: teleConsultPricing?.pricing_details?.total || 0,
      total_patient_count: patients.length,
      banner_id: '-',
      banner_label: '-',
      is_banner_active: '-',
    };

    switch (eventName) {
      case 'appointment_patient_select':
        cleverTapAnl.event({
          event_name: 'appointment_patient_select',
          event: {
            ...event,
            patient_id: selectedPatient.patientID,
            selection_mode: patientSelectTrigger.current,
          },
        });
        break;

      case 'appointment_patient_add':
      case 'appointment_patient_create':
        cleverTapAnl.event({
          event_name: eventName,
          event,
        } as EventAppointmentPatientAdd | EventAppointmentPatientCreate);
        break;

      case 'appointment_create':
        cleverTapAnl.event({
          event_name: eventName,
          event: {
            ...event,
            followup_transaction_amount: teleConsultPricing?.pricing_details?.followup_fee || 0,
            followup_available_flag: teleConsultPricing?.tag === 'FOLLOWUP_CONSULT',
            followup_used_flag: teleConsultPricing?.tag === 'FOLLOWUP_CONSULT',
            appointment_id: bookedAppointment.appointmentID || '-',
            teleconsult_id: slot.teleConsult ? bookedAppointment.id : '-',
          },
        });
        break;
      default:
        break;
    }
  }

  if (showAddPatient) {
    return (
      <AddPatient
        doctor={doctor}
        user={user}
        onSuccess={handleAddPatientSuccess}
      />
    );
  }

  if (!isEmpty(bookedAppointment)) {
    return (
      <AppointmentSuccess
        isReschedule={isReschedule}
        appointment={slot.teleConsult ? bookedAppointment.appointment : bookedAppointment}
        appointmentPricing={bookedAppointment.pricing_details}
        slot={slot}
        sessionId={slot.teleConsult ? bookedAppointment.id : undefined}
        parentCallback={parentCallback}
      />
    );
  }

  function renderContent() {
    if (!accessToken) return <BookingLogin />;

    return (
      <>
        {
          isEmpty(patients) ? (
            <ZeroPatient title={'No patient found. Please add a new patient to continue.'} />
          ) : (
            <DoconListMenu
              selected={selectedPatient}
              lists={patientsList}
              title={<Heading3 mb='12px' title='Select a Patient' fontWeight='bold' />}
              onChange={handleListMenuChange}
            />
          )
        }

        <PrimaryButton
          hidden={isReschedule}
          title='Add Patient'
          ml={'auto !important'}
          onClick={handleBtnClick('addPatient')}
        />

        <PrimaryButton
          isBlock
          icon={isBookingAppointment || isFetchingTeleConsultPricing || userUpdateStatus === Status.pending ? 'loading' : ''}
          disabled={isEmpty(patients) || isFetchingTeleConsultPricing}
          className={'confirmBtn'}
          title={'CONFIRM'}
          mt={'auto !important'}
          onClick={handleBtnClick('confirm')}
        />
      </>
    );
  }

  return (
    <Root>
      <DocItem
        picSize='48px'
        subTitle={activeClinic.name}
        doctor={doctor}
      />

      <FlexBox gap={'16px'}>
        <Image src={'/images/ic-time@3.png'} alt='timer' />
        <BaseBox flex={1}>
          <Heading3 title={slot.parsedStartTime} fontWeight='bold' />
          <Heading4 title={moment(activeDate).format('DD MMM YYYY')} />
        </BaseBox>
      </FlexBox>

      {
        isFetchingPatients ? <Loader flex={1} /> : renderContent()
      }

    </Root>
  );
}

export default ConfirmAppointment;